import { makeOptionallyAuthenticatedQuery } from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type { MaxTaxExposureDto } from '@b-stock/listing-api-client'

import getCanadianTaxClient from './getCanadianTaxClient'

/**
 * Fetches the bid-confirmation Canadian-tax max-exposure for a listing
 * (T075 endpoint on svc/listing, FR-008 of the Canadian-tax spec).
 *
 * The endpoint returns the full `MaxTaxExposureDto` — there is no
 * `taxUnavailable` discriminant the way `GET /running-estimate` has,
 * because bid-confirmation is gated on a valid `buyerAddressId` upstream
 * (the buyer cannot place a bid without one). Failure modes here mean
 * the listing-service rate lookup degraded, in which case the hook
 * suppresses (renders nothing) rather than render a stale or zero value.
 *
 * **R7 Reading B**: `committedBidAmount = max(formSingleBid, formProxyMaxBid, existingMaxBid)`
 * — the third term is read server-side from the auction's
 * `BidsApi.getAccountBids`. The form values flow in as query args (and
 * therefore the query key), so when the buyer edits the bid amount in
 * the modal the cached exposure is invalidated and a fresh one is
 * fetched.
 */
const fetchMaxTaxExposure = async (
  accessToken: string | null,
  listingId: string,
  formSingleBid: number,
  buyerAddressId: string,
  formProxyMaxBid: number | undefined,
  buyerFeeCents: number | undefined,
  shippingCents: number | undefined,
  // FP-2496 — key-only cache-bust: the server rates by the selected quote's shipment type.
  _shipmentType: string | undefined
): Promise<MaxTaxExposureDto> => {
  const client = getCanadianTaxClient()
  const baseConfig = buildAxiosRequestConfig({ accessToken })
  // FP-2498 — svc/listing !907 accepts `buyerFeeCents` + `shippingCents`
  // as optional query params to feed BUYER_FEE + SHIPPING taxable bases.
  // Piggyback them onto the axios request until listing-api-client
  // regenerates with them in the method signature.
  const extraParams: Record<string, number> = {}
  if (buyerFeeCents !== undefined) extraParams.buyerFeeCents = buyerFeeCents
  if (shippingCents !== undefined) extraParams.shippingCents = shippingCents
  const config = {
    ...baseConfig,
    params: {
      ...(baseConfig.params as Record<string, unknown>),
      ...extraParams,
    },
  }
  const { data } = await client.getMaxTaxExposure(
    { listingId, formSingleBid, buyerAddressId, formProxyMaxBid },
    config
  )
  return data
}

const canadianTaxMaxExposureQuery = makeOptionallyAuthenticatedQuery(
  'canadianTaxMaxExposure',
  fetchMaxTaxExposure
)

export default canadianTaxMaxExposureQuery
